import { isLoopbackWsUrl } from '../../../environments/ws-url';

export type WsSource = 'env' | 'derived' | 'region';

export interface WsConfigProblem {
  readonly problem: string;
  /** `ws:` | `wss:` | `none` */
  readonly ws_scheme: string;
  /** Host only — never the full URL. */
  readonly ws_host: string;
  readonly page_protocol: string;
  readonly ws_source: WsSource;
}

const URL_PARTS = /^([a-z][a-z0-9+.-]*:)\/\/([^/?#]+)/i;

/** Host only — never the full URL — for analytics properties. */
export function wsHostOf(url: string): string {
  return URL_PARTS.exec(url)?.[2] ?? '';
}

/**
 * Null when the resolved WebSocket URL can work on this page. Pure: the caller
 * passes what `environment.ts` cannot know at module load (the desktop region
 * resolver) and what it deliberately never throws about. This is the layer
 * that would have named the 19 Aug outage in one glance — a deterministic
 * statement about the bundle and the page, needing no socket, no retry and no
 * dedupe. Problem strings are URL-free; the host travels separately.
 */
export function wsConfigHealth(
  wsUrl: string,
  pageProtocol: string,
  options: { readonly source: WsSource; readonly configured?: string },
): WsConfigProblem | null {
  const parts = URL_PARTS.exec(wsUrl ?? '');
  const scheme = parts?.[1].toLowerCase() ?? 'none';
  const base = {
    ws_scheme: scheme,
    ws_host: parts?.[2] ?? '',
    page_protocol: pageProtocol,
    ws_source: options.source,
  };
  const fail = (problem: string): WsConfigProblem => ({ problem, ...base });

  if (!parts) return fail('WebSocket URL is empty or malformed');
  if (scheme !== 'ws:' && scheme !== 'wss:') {
    return fail(`WebSocket URL scheme is ${scheme}, not ws: or wss:`);
  }
  if (pageProtocol === 'https:' && scheme === 'ws:' && !isLoopbackWsUrl(wsUrl)) {
    return fail(
      'insecure ws: URL on an https: page — the browser blocks it as mixed content',
    );
  }
  if (/^tauri\.localhost$/i.test(base.ws_host)) {
    return fail(
      'WebSocket URL was derived from the Tauri webview origin; set NG_APP_WS_URL',
    );
  }
  const configured = options.configured?.trim() ?? '';
  if (
    options.source === 'env' &&
    /^(ws|http):/i.test(configured) &&
    scheme === 'wss:'
  ) {
    return fail(
      'NG_APP_WS_URL used an insecure scheme on an https: page; upgraded to wss:',
    );
  }
  return null;
}
